import React, { useRef, useState } from 'react';
import { playerApi } from '../../../services/api';

// ─── Icons ────────────────────────────────────────────────────────────────────

const UploadIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="44" height="44" rx="12" fill="#f0f0ff"/>
    <path d="M22 28V16M22 16L17 21M22 16L27 21" stroke="#0D9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 31h16" stroke="#0D9488" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// FileCheckIcon removed (unused)

const ExcelFileIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect width="15" height="15" rx="3" fill="#16a34a"/>
    <path d="M3.5 4.5l2.5 3-2.5 3H5L7 8l2 2.5H10.5L8 7.5l2.5-3H9L7 7 5 4.5H3.5z" fill="white"/>
  </svg>
);

const Spinner = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none"
    style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
    <circle cx="7" cy="7" r="5.5" stroke={color} strokeWidth="1.5" strokeDasharray="22 8" strokeLinecap="round"/>
  </svg>
);

// ─── Pill ─────────────────────────────────────────────────────────────────────

const PILL_BG    = { green: '#d1fae5', yellow: '#fef3c7', red: '#fee2e2', blue: '#dbeafe', gray: '#f1f5f9' };
const PILL_COLOR = { green: '#065f46', yellow: '#92400e', red: '#991b1b', blue: '#1d4ed8', gray: '#475569' };

const Pill = ({ label, color = 'gray' }) => (
  <span style={{
    display: 'inline-block', padding: '1px 7px', borderRadius: 999,
    fontSize: '0.7rem', fontWeight: 600,
    background: PILL_BG[color],
    color: PILL_COLOR[color],
    flexShrink: 0,
  }}>
    {label}
  </span>
);

// ─── Component ────────────────────────────────────────────────────────────────

// Steps: 'select' → 'previewing' → 'preview' → 'importing' → 'done'

const ImportEntriesModal = ({ tournamentId, onClose, onImported }) => {
  const inputRef = useRef(null);
  const [step,     setStep]     = useState('select');
  const [file,     setFile]     = useState(null);
  const [dragging, setDragging] = useState(false);
  const [preview,  setPreview]  = useState(null);   // { sheets, total_entries, total_sheets }
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState(null);

  // ── File selection → auto-preview ──────────────────────────────────────────

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    setError(null);
    setPreview(null);
    setStep('previewing');
    try {
      const res = await playerApi.previewImportEntries(tournamentId, f);
      setPreview(res.data);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not read file. Please check it is a valid Excel file.');
      setStep('select');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  // ── Confirm → import ───────────────────────────────────────────────────────

  const handleImport = async () => {
    setStep('importing');
    setError(null);
    try {
      const res = await playerApi.importEntries(tournamentId, file);
      setResult(res.data);
      setStep('done');
      onImported?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed. Please try again.');
      setStep('preview');
    }
  };

  const handleReset = () => {
    setStep('select');
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  // ── Derivations ────────────────────────────────────────────────────────────

  const recognisedSheets = preview?.sheets?.filter(s => s.recognised) ?? [];
  const skippedSheets    = preview?.sheets?.filter(s => !s.recognised) ?? [];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem',
      backdropFilter: 'blur(2px)',
    }}>
      <div style={{
        background: 'white', borderRadius: 16, width: '100%', maxWidth: 600,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
      }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '1.25rem 1.5rem 1rem', borderBottom: '1px solid #f1f5f9' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>Import Entries</h2>
            <p style={{ margin: '3px 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>
              {step === 'select' || step === 'previewing'
                ? 'Masters format Excel — one tab per category'
                : step === 'preview'
                  ? `${recognisedSheets.length} sheet${recognisedSheets.length !== 1 ? 's' : ''} · ${preview.total_entries} entries ready to import`
                  : step === 'done'
                    ? 'Import complete'
                    : 'Importing…'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#f8fafc', border: 'none', cursor: 'pointer',
              color: '#64748b', width: 28, height: 28, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1rem', lineHeight: 1, marginTop: 2, flexShrink: 0,
            }}
          >×</button>
        </div>

        <div style={{ padding: '1.25rem 1.5rem 1.5rem' }}>

          {/* ── Step: select / previewing ── */}
          {(step === 'select' || step === 'previewing') && (
            <>
              <div
                onClick={() => step === 'select' && inputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); if (step === 'select') setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${dragging ? '#0D9488' : step === 'previewing' ? '#5EEAD4' : '#e2e8f0'}`,
                  borderRadius: 12, padding: '2rem 1.5rem', textAlign: 'center',
                  cursor: step === 'select' ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                  background: dragging ? '#F0FDFA' : step === 'previewing' ? '#fafafe' : '#fafafa',
                  marginBottom: '1rem', userSelect: 'none',
                }}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={e => handleFile(e.target.files[0])}
                />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                  {step === 'previewing'
                    ? <Spinner size={36} color="#0D9488" />
                    : <UploadIcon />}

                  {step === 'previewing' ? (
                    <>
                      <div style={{ fontWeight: 600, color: '#0F766E', fontSize: '0.88rem' }}>Reading file…</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#F0FDFA', border: '1px solid #99F6E4', borderRadius: 8, padding: '0.35rem 0.75rem' }}>
                        <ExcelFileIcon />
                        <span style={{ fontWeight: 600, color: '#0F766E', fontSize: '0.82rem', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file?.name}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem', marginBottom: 4 }}>
                          {dragging ? 'Drop file here' : 'Drop your Excel file here'}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                          or <span style={{ color: '#0D9488', fontWeight: 600 }}>click to browse</span> · .xlsx or .xls · max 5 MB
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Format reminder */}
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: '1.25rem', fontSize: '0.77rem', color: '#92400e', lineHeight: 1.5 }}>
                <strong>Expected format:</strong> One tab per event (e.g. <code>MS 35+</code>, <code>WD 40+</code>, <code>XD 55+</code>).
                Columns: <em>S.No, Name, BAI ID, Club, Remarks</em> (DOB and Seed also supported).
                Doubles tabs use two consecutive rows per pair.
              </div>

              {error && (
                <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: '1rem', fontSize: '0.83rem', color: '#991b1b' }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-outline" onClick={onClose}>Cancel</button>
              </div>
            </>
          )}

          {/* ── Step: preview (confirm) ── */}
          {step === 'preview' && preview && (
            <>
              {/* Recognised sheets — entry table */}
              {recognisedSheets.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
                    Sheets to Import
                  </div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                    {/* Table header */}
                    <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 60px', gap: '0.5rem', padding: '6px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sheet</span>
                      <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Category</span>
                      <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'right' }}>Entries</span>
                    </div>
                    {recognisedSheets.map((s, i) => (
                      <div
                        key={s.name}
                        style={{
                          display: 'grid', gridTemplateColumns: '90px 1fr 60px', gap: '0.5rem',
                          padding: '8px 12px', alignItems: 'center',
                          borderBottom: i < recognisedSheets.length - 1 ? '1px solid #f1f5f9' : 'none',
                        }}
                      >
                        <code style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155' }}>{s.name}</code>
                        <span style={{ fontSize: '0.78rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.category}</span>
                        <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#059669', textAlign: 'right' }}>{s.entries}</span>
                      </div>
                    ))}
                    {/* Total row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 60px', gap: '0.5rem', padding: '7px 12px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155', gridColumn: '1 / 3' }}>Total entries</span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 800, color: '#059669', textAlign: 'right' }}>{preview.total_entries}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Skipped sheets */}
              {skippedSheets.length > 0 && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: '1rem', fontSize: '0.77rem', color: '#64748b', lineHeight: 1.6 }}>
                  <strong style={{ color: '#475569' }}>Sheets that will be skipped:</strong>{' '}
                  {skippedSheets.map(s => (
                    <span key={s.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 8 }}>
                      <code style={{ background: '#e2e8f0', borderRadius: 4, padding: '0 4px', fontSize: '0.74rem' }}>{s.name}</code>
                      <span style={{ color: '#94a3b8' }}>({s.reason})</span>
                    </span>
                  ))}
                </div>
              )}

              {error && (
                <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: '1rem', fontSize: '0.83rem', color: '#991b1b' }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                <button
                  className="btn btn-outline"
                  style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                  onClick={handleReset}
                >
                  ← Change file
                </button>
                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  <button className="btn btn-outline" onClick={onClose}>Cancel</button>
                  <button
                    className="btn btn-primary"
                    onClick={handleImport}
                    disabled={recognisedSheets.length === 0}
                  >
                    Confirm &amp; Import {preview.total_entries} entries
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── Step: importing ── */}
          {step === 'importing' && (
            <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                <Spinner size={36} color="#0D9488" />
              </div>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Importing entries…</div>
              <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>This may take a few seconds</div>
            </div>
          )}

          {/* ── Step: done (results) ── */}
          {step === 'done' && result && (
            <>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem', marginBottom: '1.25rem' }}>
                {[
                  { label: 'Players Added',   value: result.players_added,   color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
                  { label: 'Duplicates Skip', value: result.players_skipped, color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
                  { label: 'Errors',          value: result.players_errored, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
                ].map(({ label, value, color, bg, border }) => (
                  <div key={label} style={{ textAlign: 'center', padding: '0.75rem', background: bg, border: `1px solid ${border}`, borderRadius: 10 }}>
                    <div style={{ fontSize: '1.6rem', fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
                    <div style={{ fontSize: '0.68rem', color, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 3 }}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Per-sheet detail */}
              {result.detail?.length > 0 && (
                <div style={{ marginBottom: '0.875rem' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                    Per Category
                  </div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                    {result.detail.map((d, i) => (
                      <div
                        key={d.sheet}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          padding: '7px 12px', fontSize: '0.82rem',
                          borderBottom: i < result.detail.length - 1 ? '1px solid #f1f5f9' : 'none',
                        }}
                      >
                        <code style={{ fontSize: '0.77rem', fontWeight: 700, color: '#334155', minWidth: 72, flexShrink: 0 }}>{d.sheet}</code>
                        <span style={{ color: '#94a3b8', flex: 1, fontSize: '0.73rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.category}</span>
                        {d.event_matched
                          ? <Pill label="matched" color="green" />
                          : d.event_created
                            ? <Pill label="created" color="blue" />
                            : <Pill label="no event" color="yellow" />}
                        <span style={{ color: '#059669', fontWeight: 700, fontSize: '0.82rem', flexShrink: 0 }}>+{d.added}</span>
                        {d.skipped > 0 && <span style={{ color: '#b45309', fontSize: '0.72rem', flexShrink: 0 }}>skip {d.skipped}</span>}
                        {d.errored > 0 && <span style={{ color: '#dc2626', fontSize: '0.72rem', flexShrink: 0 }}>err {d.errored}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Skipped / no-event sheets */}
              {result.sheets_no_event?.length > 0 && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: '0.6rem', fontSize: '0.77rem', color: '#92400e', lineHeight: 1.5 }}>
                  <strong>No matching event found for:</strong>{' '}
                  {result.sheets_no_event.map(s => `${s.name} (${s.entries} entries)`).join(', ')}.
                  Players were still imported — create the events and re-import or assign manually.
                </div>
              )}
              {result.sheets_skipped?.length > 0 && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: '0.6rem', fontSize: '0.77rem', color: '#64748b', lineHeight: 1.5 }}>
                  <strong>Sheets skipped:</strong>{' '}
                  {result.sheets_skipped.map(s => `${s.name} (${s.reason})`).join(', ')}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button className="btn btn-outline" onClick={handleReset}>Import Another</button>
                <button className="btn btn-primary" onClick={onClose}>Done</button>
              </div>
            </>
          )}

        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ImportEntriesModal;
